import { useEffect, useState } from "react";
import { ButtonBase, Chip, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import dayjs from "dayjs";
import { PROBLEMAS, type RupturaFiltros, type RupturaResumo } from "@/api/ruptura.service";

// Peças comuns às telas da ruptura (RupturaPage, RupturaProdutosView, RupturaDetalheView).

export type NomeLoja = (codEmpresa: string | null) => string;

// Cards montados por vez nas abas (mesmo valor de app-mobile/src/app/ruptura.tsx). O resumo
// vem inteiro (o card geral soma o valor_risco de todas as linhas e a lista de produtos usa as
// opções dos filtros), mas montar milhares de cards de uma vez (aba Fornecedor) trava a página.
export const CARDS_POR_VEZ = 20;

// Mostra os itens de CARDS_POR_VEZ em CARDS_POR_VEZ: mais um lote quando o elemento em fimRef
// chega perto da tela. Trocar a chave (aba, recarregar) volta ao primeiro lote.
export function useAosPoucos<T>(itens: T[], chave: string) {
  const [estado, setEstado] = useState({ chave, qtd: CARDS_POR_VEZ });
  const [fim, setFim] = useState<HTMLElement | null>(null);
  const qtd = estado.chave === chave ? estado.qtd : CARDS_POR_VEZ;
  const temMais = qtd < itens.length;

  useEffect(() => {
    if (!fim || !temMais) return;
    // Recriado a cada lote: se o fim segue visível depois de um lote, o observer novo já avisa.
    const observer = new IntersectionObserver(
      (entries) => entries[0]?.isIntersecting && setEstado({ chave, qtd: qtd + CARDS_POR_VEZ }),
      { rootMargin: "300px" },
    );
    observer.observe(fim);
    return () => observer.disconnect();
  }, [fim, temMais, chave, qtd]);

  return { visiveis: itens.slice(0, qtd), temMais, fimRef: setFim };
}

// Abre a lista de produtos de uma linha do resumo, já com os filtros aplicados.
export type AbrirProdutos = (linha: RupturaResumo | null, filtros: RupturaFiltros) => void;

// Card geral: o "Ver por" abre as dimensões do cliente inteiro, sem filtro.
export const SEM_FILTRO: RupturaFiltros = {};

const COR_PROBLEMA: Record<number, "error" | "warning" | "info" | "success" | "default"> = {
  1: "error",
  2: "warning",
  3: "info",
  4: "info",
  5: "success",
  6: "default",
};

// Mesmas faixas do mobile (app-mobile/src/app/ruptura.tsx#perceColor).
export const corPerc = (perc: number) => (perc >= 20 ? "error" : perc >= 8 ? "warning" : "success");

export const percDe = (parte: number, total: number) => (total > 0 ? (parte / total) * 100 : 0);

export const formatNumero = (valor: number | null | undefined, casas = 2) =>
  valor === null || valor === undefined ? "—" : valor.toLocaleString("pt-BR", { maximumFractionDigits: casas });

export const formatData = (valor: string | null | undefined) => (valor ? dayjs(valor).format("DD/MM/YYYY") : "—");

export const formatDataHora = (valor: string | null | undefined) => (valor ? dayjs(valor).format("DD/MM/YYYY HH:mm") : "—");

export function ProblemaChip({ codProblema, nome }: { codProblema: number; nome: string }) {
  return <Chip size="small" label={nome || PROBLEMAS[codProblema]} color={COR_PROBLEMA[codProblema] ?? "default"} />;
}

export function RodapeCard({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <ButtonBase onClick={onClick} sx={{ width: "100%", borderTop: "1px solid", borderColor: "divider", py: 1 }}>
      <Typography variant="caption" fontWeight={600} color="primary.main">
        {label}
      </Typography>
    </ButtonBase>
  );
}

export function Voltar({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <ButtonBase onClick={onClick} sx={{ alignSelf: "flex-start", gap: 0.5, color: "primary.main", borderRadius: 1 }}>
      <ChevronLeft fontSize="small" />
      <Typography variant="body2" fontWeight={600}>
        {label}
      </Typography>
    </ButtonBase>
  );
}
